import { useTranslation } from 'react-i18next';

import { StatCard } from '@nucleus-commerce/admin-kit/layout';
import type { ReportTotals } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ChangeIndicator } from './change-indicator';

export type KpiKey =
  | 'gross'
  | 'orders'
  | 'aov'
  | 'net'
  | 'units'
  | 'newCustomers'
  | 'customers'
  | 'sessions'
  | 'conversionRate';

export interface KpiGridProps {
  kpis: readonly KpiKey[];
  totals: ReportTotals | undefined;
  previous: ReportTotals | undefined;
  isLoading: boolean;
  className?: string;
  /** `data-tour` value when a guided tour points at the grid. */
  tour?: string;
}

interface Figure {
  value: string;
  /** Null when the figure does not exist for the period (no sessions, no conversion rate). */
  current: number | null;
  previous: number | null;
}

/** Each figure next to its change against the previous period of the same length. */
export function KpiGrid({ kpis, totals, previous, isLoading, className, tour }: KpiGridProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();

  const figure = (key: KpiKey, now: ReportTotals, before: ReportTotals): Figure => {
    switch (key) {
      case 'gross':
        return {
          value: format.money(now.gross),
          current: now.gross.amount,
          previous: before.gross.amount,
        };
      case 'net':
        return {
          value: format.money(now.net),
          current: now.net.amount,
          previous: before.net.amount,
        };
      case 'aov':
        return {
          value: format.money(now.aov),
          current: now.aov.amount,
          previous: before.aov.amount,
        };
      case 'orders':
        return {
          value: format.number(now.ordersCount),
          current: now.ordersCount,
          previous: before.ordersCount,
        };
      case 'units':
        return { value: format.number(now.units), current: now.units, previous: before.units };
      case 'newCustomers':
        return {
          value: format.number(now.newCustomers),
          current: now.newCustomers,
          previous: before.newCustomers,
        };
      case 'customers':
        return {
          value: t('kpi.customersValue', {
            new: format.number(now.newCustomers),
            returning: format.number(now.returningCustomers),
          }),
          current: now.newCustomers,
          previous: before.newCustomers,
        };
      case 'sessions':
        return {
          value: format.number(now.sessions),
          current: now.sessions,
          previous: before.sessions,
        };
      case 'conversionRate':
        return {
          value:
            now.conversionRate === null
              ? '—'
              : format.percent(now.conversionRate, { maximumFractionDigits: 2 }),
          current: now.conversionRate,
          previous: before.conversionRate,
        };
    }
  };

  return (
    <div className={cn('grid grid-cols-2 gap-4 lg:grid-cols-3', className)} data-tour={tour}>
      {kpis.map((key) => {
        const f = totals && previous ? figure(key, totals, previous) : undefined;
        return (
          <div key={key} role="group" aria-label={t(`kpi.${key}`)} className="contents">
            <StatCard
              label={t(`kpi.${key}`)}
              value={f?.value ?? '—'}
              detail={f ? <ChangeIndicator current={f.current} previous={f.previous} /> : ''}
              isLoading={isLoading}
            />
          </div>
        );
      })}
    </div>
  );
}
